import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieGridProps {
  title: string;
  subtitle?: string;
  icon: string;
  movies: Movie[];
  accentClass?: string;
}

export default function MovieGrid({ title, subtitle, icon, movies, accentClass = 'text-primary-500' }: MovieGridProps) {
  if (movies.length === 0) return null;

  return (
    <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
      {/* Header */}
      <div className="flex items-end justify-between mb-6 md:mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${icon} ${accentClass} text-sm`}></i>
            </div>
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">{title}</h2>
          </div>
          {subtitle && <p className="text-sm text-foreground-500 ml-7">{subtitle}</p>}
        </div>
        <Link
          to="/category/zuixin"
          className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
        >
          查看更多
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-xs"></i>
          </div>
        </Link>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
        {movies.map((movie) => (
          <Link
            key={movie.id}
            to={`/article/${movie.id}`}
            className="group cursor-pointer"
          >
            {/* Poster */}
            <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3">
              <img
                src={movie.image}
                alt={movie.title}
                title={`${movie.title} - 爱丫爱丫影院高清在线观看`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
              />
              {/* Badges */}
              <div className="absolute top-2 left-2 flex flex-wrap gap-1.5">
                {movie.isNew && (
                  <span className="px-2 py-0.5 bg-primary-500 text-white text-[10px] font-semibold rounded-md whitespace-nowrap">
                    NEW
                  </span>
                )}
                {movie.isHot && (
                  <span className="px-2 py-0.5 bg-accent-500 text-foreground-900 text-[10px] font-semibold rounded-md whitespace-nowrap">
                    热播
                  </span>
                )}
              </div>
              {/* Rating */}
              <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/60 backdrop-blur-sm text-accent-400 text-xs font-semibold rounded-md">
                <i className="ri-star-fill text-[10px] mr-0.5"></i>
                {movie.rating}
              </div>
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-play-fill text-primary-500 text-xl ml-0.5"></i>
                </div>
              </div>
            </div>
            {/* Info */}
            <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
              {movie.title}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-foreground-400">{movie.year}</span>
              <span className="text-[10px] text-foreground-300">·</span>
              <span className="text-xs text-foreground-400">{movie.duration}</span>
            </div>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {movie.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-background-100 text-foreground-500 text-[10px] rounded-md whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}